import React, { useContext, useEffect, useState } from 'react';
import { IMemberSalesOrderInfoResult } from '../utils/types';
import {
  Box,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChevronRightTwoTone } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { individualBalances } from '../api';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import { AuthContext } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';

export function GuidanceToursContainer() {
  const [data, setData] = useState<IMemberSalesOrderInfoResult[]>();
  const { t } = useTranslation();
  const { token } = useContext(AuthContext);
  const [submitting, setSubmitting] = useState(false);
  const showSnackbar = useAlertSnackbar();

  useEffect(() => {
    fetchBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBalances = async () => {
    setSubmitting(true);
    try {
      const orders: IMemberSalesOrderInfoResult[] = await individualBalances(
        true,
        token,
      );
      const filteredOrders = orders
        .filter((order) => order.positionTypeCode !== 'CUS')
        .sort((a, b) => {
          return (
            new Date(b.tourStartDate).getTime() -
            new Date(a.tourStartDate).getTime()
          );
        });
      setData(filteredOrders);
    } catch (e: any) {
      showSnackbar(e.detail, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const calculateDaysRemaining = (startDate: string | Date) => {
    const today = new Date();
    const tourDate = new Date(startDate);
    const diffTime = tourDate.getTime() - today.getTime();
    const diffDay = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDay;
  };

  return (
    <Box>
      {submitting && (
        <LoadingSpinner loadingText={t('balancesQueryPage.loading')} />
      )}
      {data?.length == 0 && !submitting && (
        <Typography>{t('ordersContainer.noRecord')}</Typography>
      )}
      <Typography variant="h5" sx={{ mb: 1 }}>
        {t('GuidanceToursContainer.tours')}
      </Typography>
      <Divider />
      <List>
        <ListSubheader>{t('GuidanceToursContainer.description')}</ListSubheader>
        {data?.map((item) => {
          const diffDaysFromStartDate = calculateDaysRemaining(
            item.tourStartDate,
          );
          const diffDaysFromEndDate = calculateDaysRemaining(item.tourEndDate);
          const beforeTourDayCount = 7;
          const afterTourDayCount = 30;
          const isDisabled =
            diffDaysFromStartDate > afterTourDayCount ||
            diffDaysFromEndDate < -beforeTourDayCount;
          const isMoreThanFortyDays = diffDaysFromStartDate > afterTourDayCount;
          const isTenDaysPassed = diffDaysFromEndDate < -beforeTourDayCount;
          return (
            <>
              <ListItemButton
                component={Link}
                to={`/account/guidance/${item.salesIndividualId}?tourId=${item.tourId}`}
                key={item.orderNumber}
                disabled={isDisabled}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    width: '100%',
                  }}
                >
                  <ListItemText
                    primary={item.tourInfo}
                    secondary={new Date(item.tourStartDate)
                      .toLocaleString('tr-TR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      } as Intl.DateTimeFormatOptions)
                      .toString()}
                  />
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                        }}
                      >
                        {isMoreThanFortyDays && (
                          <Chip
                            variant="filled"
                            color="warning"
                            sx={{ mt: -1, fontSize: '11px' }}
                            label={t(
                              'GuidanceToursContainer.moreThanFortyDays',
                              {
                                days: diffDaysFromStartDate - afterTourDayCount,
                              },
                            )}
                          />
                        )}
                        {isTenDaysPassed && (
                          <Chip
                            variant="filled"
                            color="warning"
                            sx={{ mt: -1, fontSize: '10px' }}
                            label={t('GuidanceToursContainer.tenDaysPassed')}
                          />
                        )}
                      </Box>
                    }
                  />
                </Box>
                {!isDisabled && <ChevronRightTwoTone />}
              </ListItemButton>
            </>
          );
        })}
      </List>
    </Box>
  );
}
